// ============================================================================
// 릴리즈 + 워크플로우 React Query 훅 (CHG-048)
// ============================================================================

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import * as releaseApi from '../services/releaseService';

export const releaseKeys = {
  list: (projectId: string) => ['releases', projectId] as const,
  detail: (projectId: string, releaseId: string) => ['releases', projectId, releaseId] as const,
  workflows: (projectId: string, issueId: string) => ['workflows', projectId, issueId] as const,
  byIssue: (projectId: string, issueId: string) => ['releases', 'by-issue', projectId, issueId] as const,
};

export function useReleases(projectId: string, status?: string) {
  return useQuery({
    queryKey: [...releaseKeys.list(projectId), status],
    queryFn: () => releaseApi.fetchReleases(projectId, status),
    enabled: !!projectId,
  });
}

export function useReleaseDetail(projectId: string, releaseId: string) {
  return useQuery({
    queryKey: releaseKeys.detail(projectId, releaseId),
    queryFn: () => releaseApi.fetchReleaseDetail(projectId, releaseId),
    enabled: !!projectId && !!releaseId,
  });
}

export function useCreateRelease(projectId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: { version: string; title: string; description?: string; targetDate?: string }) =>
      releaseApi.createRelease(projectId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: releaseKeys.list(projectId) }); },
  });
}

export function useUpdateRelease(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Record<string, unknown>) => releaseApi.updateRelease(projectId, releaseId, data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: releaseKeys.detail(projectId, releaseId) });
      qc.invalidateQueries({ queryKey: releaseKeys.list(projectId) });
    },
  });
}

export function useAddReleaseIssues(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { issueIds: string[]; includeDescendants?: boolean }) =>
      releaseApi.addReleaseIssues(projectId, releaseId, input.issueIds, { includeDescendants: input.includeDescendants }),
    onSuccess: (_, vars) => {
      qc.invalidateQueries({ queryKey: releaseKeys.detail(projectId, releaseId) });
      qc.invalidateQueries({ queryKey: releaseKeys.list(projectId) });
      // 영향 받은 이슈들의 by-issue 캐시 무효화 — 해당 이슈와 그 후손 모두
      vars.issueIds.forEach((id) => {
        qc.invalidateQueries({ queryKey: releaseKeys.byIssue(projectId, id) });
      });
    },
  });
}

export function useIssueReleases(projectId: string, issueId: string) {
  return useQuery({
    queryKey: releaseKeys.byIssue(projectId, issueId),
    queryFn: () => releaseApi.fetchIssueReleases(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}

export function useRemoveReleaseIssue(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (issueId: string) => releaseApi.removeReleaseIssue(projectId, releaseId, issueId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: releaseKeys.detail(projectId, releaseId) }); },
  });
}

// --- CHG-100: 릴리즈 TC 관리 훅 ---

export function useReleaseTestMetrics(projectId: string, releaseId: string) {
  return useQuery({
    queryKey: ['release-test-metrics', projectId, releaseId] as const,
    queryFn: () => releaseApi.fetchReleaseTestMetrics(projectId, releaseId),
    enabled: !!projectId && !!releaseId,
  });
}

export function useRescanTestCases(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => releaseApi.rescanTestCases(projectId, releaseId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['release-test-metrics', projectId, releaseId] }); },
  });
}

export function useCreateReleaseTestCase(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, data }: { issueId: string; data: Parameters<typeof releaseApi.createReleaseTestCase>[3] }) =>
      releaseApi.createReleaseTestCase(projectId, releaseId, issueId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['release-test-metrics', projectId, releaseId] }); },
  });
}

export function useLinkTestCases(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, testCaseIds }: { issueId: string; testCaseIds: string[] }) =>
      releaseApi.linkTestCases(projectId, releaseId, issueId, testCaseIds),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['release-test-metrics', projectId, releaseId] }); },
  });
}

export function useUnlinkTestCase(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ issueId, testCaseId }: { issueId: string; testCaseId: string }) =>
      releaseApi.unlinkTestCase(projectId, releaseId, issueId, testCaseId),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['release-test-metrics', projectId, releaseId] }); },
  });
}

export function useExecuteReleaseTest(projectId: string, releaseId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (data: Parameters<typeof releaseApi.executeReleaseTest>[2]) =>
      releaseApi.executeReleaseTest(projectId, releaseId, data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['release-test-metrics', projectId, releaseId] }); },
  });
}

export function useIssueWorkflows(projectId: string, issueId: string) {
  return useQuery({
    queryKey: releaseKeys.workflows(projectId, issueId),
    queryFn: () => releaseApi.fetchIssueWorkflows(projectId, issueId),
    enabled: !!projectId && !!issueId,
  });
}
